"use client";

import { OTP_TYPE } from "@/common/const";
import { useAuth } from "@/contexts/Auth";
import { useGlobalData } from "@/contexts/GlobalData";
import { useNotify } from "@/contexts/NotifyProvider";
import { cn } from "@/lib/utils";
import sendOTPClaimService from "@/services/sendOTPClaimService";
import { TRANSACTION_TYPE } from "@/types/transactionType";
import openLinkInTelegram from "@/utils/openLinkInTelegram";
import { validateOtp } from "@/utils/validate";
import { CircularProgress, Divider, InputOtp, Tab } from "@nextui-org/react";
import { useRouter } from "next/navigation";
import { HTMLAttributes, useEffect, useRef, useState } from "react";
import ChipButton, { ButtonStatus } from "../ChipButton";
import CustomTabs from "../CustomTabs";
import DisabledTab from "../DisabledTab";
import DynamicFontSize from "../DynamicFontSize";
import InputField from "../InputField";
import PrimaryButton from "../PrimaryButton";
import UnderlineText from "../UnderlineText";
import OtpForm from "../OtpForm";
import OtpInputComponent from "../OtpInput";
import clsx from "clsx";
import { Keyboard, KeyboardRef, KeyboardType, THEME } from "tek-input-keyboard";
import scrollToElementIfNeeded from "@/common/function/scrollToElementIfNeeded";
type ComfirmClaimContentHelperProps = {
  contentTitle?: string;
  contentDescription?: string;
  handleAction?: () => void;
  functionPrefix?: string;
  buttonClassName?: string;
} & HTMLAttributes<HTMLDivElement>;
export const ComfirmClaimContentHelper = ({
  contentTitle,
  contentDescription,
  className,
  handleAction,
  functionPrefix,
  buttonClassName
}: ComfirmClaimContentHelperProps) => {
  const {
    triggerHapticFeedBack
  } = useGlobalData();
  const router = useRouter();
  return <div className={cn("flex flex-col gap-4 items-center text-center", className)} data-sentry-component="ComfirmClaimContentHelper" data-sentry-source-file="index.tsx">
            <DynamicFontSize fontSize={16} className="!font-medium text-white leading-[1.4]" data-sentry-element="DynamicFontSize" data-sentry-source-file="index.tsx">
                {contentTitle}
            </DynamicFontSize>
            <DynamicFontSize fontSize={13} className="!font-medium text-description leading-[1.4]" data-sentry-element="DynamicFontSize" data-sentry-source-file="index.tsx">
                {contentDescription}
            </DynamicFontSize>
            <PrimaryButton className={clsx("w-full", buttonClassName)} onClick={() => {
      triggerHapticFeedBack("success");
      router.push("/setting?current_setting_tab=security");
      handleAction && handleAction();
    }} status={"normal"} data-sentry-element="PrimaryButton" data-sentry-source-file="index.tsx">
                Continue
            </PrimaryButton>
        </div>;
};
type ComfirmClaimContentProps = {
  handleChange: (value: string) => void;
  handleClick?: () => void;
  handlePaste: () => void;
  contentTitle?: string;
  contentDescription?: string;
  errorMessage?: string;
  sendCodeStatus: ButtonStatus;
  minutes?: number;
  seconds?: number;
  value?: string;
} & HTMLAttributes<HTMLDivElement>;
function ComfirmClaimContent({
  handleChange,
  handleClick,
  handlePaste,
  contentTitle,
  contentDescription,
  errorMessage,
  sendCodeStatus,
  className,
  minutes,
  seconds,
  value
}: ComfirmClaimContentProps) {
  const {
    translate,
    triggerHapticFeedBack
  } = useGlobalData();
  return <div className={cn("flex flex-col gap-4", className)} data-sentry-component="ComfirmClaimContent" data-sentry-source-file="index.tsx">
            <DynamicFontSize fontSize={16} className="!font-medium text-white leading-[1.4]" data-sentry-element="DynamicFontSize" data-sentry-source-file="index.tsx">
                {contentTitle}
            </DynamicFontSize>
            <DynamicFontSize fontSize={13} className="!font-medium text-description leading-[1.4]" data-sentry-element="DynamicFontSize" data-sentry-source-file="index.tsx">
                {contentDescription}
            </DynamicFontSize>
            <InputField inputProps={{
      type: "number",
      id: "code",
      value: value,
      required: true,
      placeholder: translate("code placeholder"),
      variant: "bordered",
      radius: "md",
      onValueChange: handleChange,
      classNames: {
        inputWrapper: cn("py-2 h-[44px] w-full", errorMessage && "!border-error"),
        input: cn("text-base leading-[1.2] placeholder:text-white/[0.16] text-white", errorMessage && "!text-error"),
        errorMessage: errorMessage && "text-error"
      },
      errorMessage: errorMessage,
      isInvalid: !!errorMessage,
      endContent: <div className="flex self-stretch gap-2">
                            {handleClick && <>
                                    <Divider orientation="vertical" className="bg-description" />

                                    <ChipButton onClick={() => {
            triggerHapticFeedBack("success");
            handleClick();
          }} radius="md" status={sendCodeStatus} isDisabled={minutes != 0 || seconds != 0} color="secondary" variant="flat" classNames={{
            content: "text-xs text-secondary"
          }}>
                                        {translate("btn send")}
                                    </ChipButton>
                                </>}
                        </div>
    }} data-sentry-element="InputField" data-sentry-source-file="index.tsx" />
            <div className="flex items-center justify-between">
                <UnderlineText className="text-xs !text-description font-normal" onClick={() => {
        if (translate("otp description link").includes("/")) {
          openLinkInTelegram(translate("otp description link"));
        }
      }} data-sentry-element="UnderlineText" data-sentry-source-file="index.tsx">
                    {translate("otp description")}
                </UnderlineText>
                {handleClick && (minutes != 0 || seconds != 0) && <div className="flex items-center gap-1 text-[13px] leading-[1.2]">
                        <p className="text-description">Resend</p>
                        {minutes == 0 && seconds == 0 ? "" : <span className="text-white">{`${Number(minutes) < 10 ? `0${minutes}` : minutes}:${Number(seconds) < 10 ? `0${seconds}` : seconds}`}</span>}
                    </div>}
            </div>
        </div>;
}
type ComfirmClaimProps = {
  handleClaim: (otp: string, type: TRANSACTION_TYPE | OTP_TYPE, typeOTP?: string) => void;
  claimType: TRANSACTION_TYPE | OTP_TYPE;
  amount: number;
  packageKey?: string;
  claimStatus?: ButtonStatus;
  errorMessage?: string;
  setErrorMessage: (value: string) => void;
  functionPrefix?: string;
  hideDivider?: boolean;
  buttonClassName?: string;
  boardClassName?: string;
  autoSubmit?: boolean;
  autoSendCodeToEmail?: boolean;
  overrideOtp?: string;
  alwayFocus?: boolean;
} & HTMLAttributes<HTMLDivElement>;
function ComfirmClaim({
  amount,
  claimType,
  handleClaim,
  packageKey,
  claimStatus = "normal",
  errorMessage,
  setErrorMessage,
  functionPrefix = "confirm_otp_",
  className,
  hideDivider,
  buttonClassName,
  boardClassName,
  autoSubmit,
  autoSendCodeToEmail,
  overrideOtp,
  alwayFocus
}: ComfirmClaimProps) {
  const prefix = "otp_form_";
  const {
    otpComfirmationData,
    translate,
    authenInfo,
    triggerHapticFeedBack,
    runEffectNavigation
  } = useGlobalData();
  const {
    token
  } = useAuth();
  const [otp, setOtp] = useState("");
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [sendCodeStatus, setSendCodeStatus] = useState<ButtonStatus>("normal");
  const [alwaysFocusOTP, setAlwaysFocusOTP] = useState(false);
  const noti = useNotify();
  const handleChange = (value: string) => {
    setErrorMessage("");
    setOtp(value);
    if (autoSubmit && value?.length == 6) {
      handleClaim?.(value, claimType, OTP_TYPE.EMAIL);
    }
  };
  const handlePasteClipboard = async () => {
    try {
      const text = await window.navigator.clipboard.readText();
      setOtp(text);
    } catch (error) {
      console.log("🚀 ~ Not supported ~ error:", error);
    }
  };
  const sendVerificationCode = () => {
    triggerHapticFeedBack("success");
    setSendCodeStatus("loading");
    try {
      sendOTPClaimService(token, claimType, String(amount), otpComfirmationData?.find((item: any) => {
        return item.type == claimType;
      })?.currency?.symbol, packageKey, res => {
        if (res.success) {
          const currentDate = Date.now();
          const minutesRes = new Date(new Date(res.data.timeRetry).getTime() - currentDate).getMinutes();
          const secondesRes = new Date(new Date(res.data.timeRetry).getTime() - currentDate).getSeconds();
          localStorage.setItem(`countdown-otp-claim-${claimType}`, res.data.timeRetry);
          setSeconds(secondesRes);
          setMinutes(minutesRes);
          triggerHapticFeedBack("success");
          noti?.success(translate("check email"), {
            duration: 2000
          });
          setSendCodeStatus("success");
          setTimeout(() => {
            setSendCodeStatus("normal");
          }, 2000);
        } else {
          triggerHapticFeedBack("error");
          noti?.error(translate(res.message), {
            duration: 2000
          });
          setSendCodeStatus("error");
          setTimeout(() => {
            setSendCodeStatus("normal");
          }, 2000);
        }
      });
    } catch (error) {
      console.log("🚀 ~ sendVerificationCode ~ error:", error);
      setSendCodeStatus("error");
      setTimeout(() => {
        setSendCodeStatus("normal");
      }, 2000);
    }
  };
  useEffect(() => {
    if (autoSendCodeToEmail) {
      const time = localStorage.getItem(`countdown-otp-claim-${claimType}`);
      if (!time) {
        sendVerificationCode();
        return;
      }
      const untilTime = new Date(time)?.getTime();
      const currentTime = new Date()?.getTime();
      if (currentTime > untilTime) {
        sendVerificationCode();
      }
    }
  }, [autoSendCodeToEmail]);
  useEffect(() => {
    const countdownOtp = localStorage.getItem(`countdown-otp-claim-${claimType}`);
    if (countdownOtp) {
      const currentDate = Date.now();
      const countdownOtpTime = new Date(countdownOtp)?.getTime();
      if (currentDate >= countdownOtpTime) return;
      const calculateTime = new Date(countdownOtp).getTime() - currentDate;
      const minutesRes = calculateTime > 0 ? new Date(calculateTime).getMinutes() : 0;
      const secondesRes = calculateTime > 0 ? new Date(calculateTime).getSeconds() : 0;
      setSeconds(secondesRes);
      setMinutes(minutesRes);
      // localStorage.removeItem(`countdown-otp-claim-${claimType}`);
    }
  }, [claimType]);
  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(interval);
        } else {
          setSeconds(59);
          setMinutes(minutes - 1);
        }
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [seconds]);
  useEffect(() => {
    if (overrideOtp != undefined) {
      handleChange(overrideOtp);
    }
  }, [overrideOtp]);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const keyboardRef = useRef<KeyboardRef>(null);
  const onCompleteInputting = () => {
    handleClaim(otp, claimType, OTP_TYPE.EMAIL);
    runEffectNavigation();
  };
  useEffect(() => {
    if (!alwayFocus) return;
    const tm = setTimeout(() => {
      setAlwaysFocusOTP(true);
    }, 800);
    return () => {
      clearTimeout(tm);
    };
  }, []);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const onOpenKeyboard = (height?: number) => {
    setKeyboardHeight(height || 0);
    setTimeout(() => {
      scrollToElementIfNeeded(inputContainerRef.current);
    }, 300);
  };
  return <div className={cn("flex flex-col gap-4", className)} data-sentry-component="ComfirmClaim" data-sentry-source-file="index.tsx">
            {authenInfo?.status == "active" ? <>
                    <div className={cn("flex flex-col gap-3 justify-center", {
        "flex-1 ": !!keyboardHeight
      }, boardClassName)}>
                        <DynamicFontSize fontSize={16} className="!font-medium text-white leading-[1.4]">
                            {translate("Email Code")}
                        </DynamicFontSize>
                        <DynamicFontSize fontSize={13} className="!font-medium text-description leading-[1.4]">
                            {translate("You need to wait until the day you can withdraw your principal")}
                        </DynamicFontSize>
                        <div className="w-[350px] px-4 self-center">
                            <Keyboard keyboardType={KeyboardType.Text} alwaysOpen={alwaysFocusOTP} ref={keyboardRef} onOpen={onOpenKeyboard} onClose={() => {
            setKeyboardHeight(0);
          }} classNames={{
            keyboardContainer: "z-[9999999]"
          }} theme={THEME.DARK} onChange={value => {
            if (value?.length > 6) {
              return;
            }
            handleChange(value);
          }} value={otp} trigger={<div className="relative" ref={inputContainerRef}>
                                        <InputOtp length={6} value={otp} variant="bordered" className="w-full text-white" size={undefined} classNames={{
              input: "text-white ",
              segmentWrapper: "w-full justify-between",
              segment: "bg-white/[0.16] !text-white border-red !size-12 data-[focus=true]:!size-12 data-[focus=true]:scale-100 rounded-2xl data-[focus=true]:!border data-[focus:true]:!border-description border !border-description",
              caret: "bg-white"
            }} radius={undefined} isReadOnly onComplete={onCompleteInputting} disabled={claimStatus != "loading"} />
                                        {claimStatus == "loading" && <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                                <CircularProgress aria-label="Loading..." color="secondary" classNames={{
                svg: "w-6 h-6"
              }} />
                                            </div>}
                                        <div className="absolute inset-0 z-[9999]"></div>
                                    </div>} />
                        </div>
                        {errorMessage && <DynamicFontSize fontSize={12} className="italic text-error font-light">
                                {translate(prefix + errorMessage)}
                            </DynamicFontSize>}
                        <div className="flex items-center justify-between w-full">
                            <UnderlineText className="text-xs !text-description font-normal" onClick={() => {
            if (translate("otp description link").includes("/")) {
              openLinkInTelegram(translate("otp description link"));
            }
          }}>
                                {translate("otp description")}
                            </UnderlineText>
                            {minutes == 0 && seconds == 0 ? <ChipButton onClick={sendVerificationCode} as={"button"} isDisabled={sendCodeStatus != "normal"} status={sendCodeStatus || "normal"} buttonType="submit" color="secondary" variant="flat" radius="md" classNames={{
            base: "ml-auto",
            content: "text-secondary"
          }}>
                                    {translate(prefix + "Send code")}
                                </ChipButton> : <div className="flex items-center gap-1 text-[13px] leading-[1.2] ml-auto">
                                    <p className="text-description">{translate(prefix + "Resend")}</p>
                                    {minutes == 0 && seconds == 0 ? "" : <span className="text-white">
                                            {minutes < 10 ? `0${minutes}` : minutes}:
                                            {seconds < 10 ? `0${seconds}` : seconds}
                                        </span>}
                                </div>}
                        </div>
                        {/* {!hideDivider && <div className="divider" />}
                         <PrimaryButton
                         onClick={() => {
                            triggerHapticFeedBack("success");
                            handleClaim(otp, claimType, OTP_TYPE.EMAIL);
                         }}
                         status={!otp || otp.length != 6 || errorMessage ? "disabled" : claimStatus}
                         statusKey={functionPrefix + "Continue"}
                         className={clsx(buttonClassName)}
                         ></PrimaryButton> */}
                    </div>
                    <div className="duration-300" style={{
        height: `${keyboardHeight}px`
      }}></div>
                </> : <ComfirmClaimContentHelper className="pt-4" contentTitle={translate("helper email heading")} contentDescription={translate("helper email description")} />}
        </div>;
}
export default ComfirmClaim;